import { useEffect, useState } from 'react';
import {
  CachedShareTableEntry,
  CachedTenderShareDetails,
} from '~components/atoms/CachedSharesTable/ProjectWebCachedSharesTable';
import { getShareDataWithCacheKey } from '~components/atoms/CachedSharesTable/utils';

type ExpiredCachedShare = {
  shareId?: string;
  shareKey?: string | null;
};

export const useSharesSimultaneously = (
  tenderSharesListFromLocalStorage: CachedTenderShareDetails[]
) => {
  const [sharesList, setSharesList] = useState<CachedShareTableEntry[]>([]);

  // IMPORTANT:
  // We must track expired invitations by the exact pair shareId + shareKey.
  // Reason:
  // the same tender/shareId can exist with multiple different invitation keys,
  // and removing by shareId only would wrongly delete all invitations for that tender.
  const [expiredSharesList, setExpiredSharesList] = useState<
    ExpiredCachedShare[]
  >([]);

  useEffect(() => {
    const getSharesListSimultaneously = async (
      sharedTendersList: CachedTenderShareDetails[]
    ) => {
      const activeTenderSharesResult = await Promise.allSettled(
        sharedTendersList.map(async ({ shareId, shareKey }) => {
          // Load each cached invitation using its exact cache key pair.
          const shareData = await getShareDataWithCacheKey(shareId, shareKey);

          return shareData ? { ...shareData, shareId, shareKey } : undefined;
        })
      );

      const activeTenderShares: CachedShareTableEntry[] = [];
      const expiredTenderShares: ExpiredCachedShare[] = [];

      let i = 0;
      for (const tenderShareResult of activeTenderSharesResult) {
        // IMPORTANT:
        // Keep the full original invitation object for this iteration,
        // not only shareId, because shareKey is also required to identify
        // the exact cached invitation entry.
        const currentTenderShare = sharedTendersList[i];

        if (
          tenderShareResult.status === 'fulfilled' &&
          !!tenderShareResult.value
        ) {
          activeTenderShares.push({
            ...tenderShareResult.value,
            attachments: [],
          });
        } else if (
          // If backend rejects this exact cached invitation (403),
          // mark this exact shareId + shareKey pair as expired.
          // Do NOT remove by shareId only.
          tenderShareResult.status === 'rejected' &&
          tenderShareResult.reason.status === 403 &&
          currentTenderShare?.shareId
        ) {
          expiredTenderShares.push({
            shareId: currentTenderShare.shareId,
            shareKey: currentTenderShare.shareKey,
          });
        }

        i++;
      }

      setSharesList(activeTenderShares);
      setExpiredSharesList(expiredTenderShares);
    };

    if (tenderSharesListFromLocalStorage.length > 0) {
      getSharesListSimultaneously(tenderSharesListFromLocalStorage);
      return;
    }

    // Keep state clean when local storage list becomes empty.
    setSharesList([]);
    setExpiredSharesList([]);
  }, [tenderSharesListFromLocalStorage]);

  return { sharesList, expiredSharesList };
};
